// invodiff-app.jsx — root app, state, top bar with nav tabs + theme switch.
import { TradeScopeSelector } from './invodiff-jobs.jsx';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { firebase, fbAuth, fbDb } from './firebase.js';
import { ErrorBoundary } from './error-boundary.jsx';
import { LoginScreen } from './firebase-auth.jsx';
import { useTweaks, TweaksPanel, TweakSection, TweakRadio, TweakToggle, TweakColor, TweakButton } from './tweaks-panel.jsx';
import { Icon, InputView } from './invodiff-input.jsx';
import { JobListView } from './invodiff-jobs.jsx';
import { SummaryView } from './invodiff-compare.jsx';
import { DrawdownView } from './invodiff-drawdown.jsx';
import { VarianceView } from './variance-engine.jsx';
import { PriceHistoryView } from './price-history.jsx';
import { VendorsView } from './invodiff-vendors.jsx';
import { AdminPanel } from './admin-panel.jsx';
import { useVendorRules, useJobsStore, getJobTrades, ROLE_LABEL, ROLE_COLOR } from './invodiff-data.jsx';
import { useRoute } from './route.js';

// Approval workflow components resolve via window: while APPROVALS_ENABLED is
// false the approval-workflow module is not imported anywhere, stays out of
// the bundle, and these are undefined — every use below is flag-gated. To
// re-enable: flip the flag (invodiff-data.jsx) and add
//   import './approval-workflow.jsx';
// here (it self-registers these on window).
const { NotificationBell, ApprovalView, ApprovalWorkflowStyles } = (typeof window !== 'undefined' ? window : {});

// ── Auth state hook ──────────────────────────────────────────────────────
function useAuthState() {
  const [user, setUser] = React.useState(undefined); // undefined = still checking
  React.useEffect(() => {
    return fbAuth.onAuthStateChanged((u) => setUser(u ?? null));
  }, []);
  return user;
}

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "theme": "light",
  "density": "comfortable",
  "accent": "steel",
  "seedHistory": false
}/*EDITMODE-END*/;

// Accent palette is theme-split. The dark-theme values are LIGHT (paired with a
// dark --accent-fg); reusing them in light mode put white text on a too-light
// blue (failed WCAG AA) AND made accent-as-text/border illegible on white.
// ACCENT_MAP_LIGHT carries darker, verified-AA variants (white --accent-fg
// clears 4.5:1 for every hue). ACCENT_MAP stays the canonical key→swatch used
// by the picker. See useEffect below + DESIGN-SYSTEM.md.
const ACCENT_MAP = {
  steel:  'oklch(0.72 0.10 240)',
  emerald:'oklch(0.74 0.10 165)',
  amber:  'oklch(0.80 0.13 75)',
  violet: 'oklch(0.72 0.11 300)',
  neutral:'oklch(0.86 0.005 240)',
};
const ACCENT_MAP_LIGHT = {
  steel:  'oklch(0.52 0.10 240)',
  emerald:'oklch(0.52 0.13 165)',
  amber:  'oklch(0.55 0.14 75)',
  violet: 'oklch(0.52 0.16 300)',
  neutral:'oklch(0.45 0.01 240)',
};

// ── Theme switch ────────────────────────────────────────────────────────
function ThemeSwitch({ value, onChange }) {
  const isDark = value === 'dark';
  return (
    <button
      className="theme-switch"
      onClick={() => onChange(isDark ? 'light' : 'dark')}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <span className="theme-switch-track">
        <span className="theme-switch-thumb" data-on={isDark ? 'dark' : 'light'}>
          {isDark
            ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
          }
        </span>
      </span>
    </button>
  );
}

// Count vendors that actually have rules — excluding the empty-vendor ('') key
// that vendorless-quote rules fall under, so the Settings badge stays stable
// and the TopBar + Settings panel always agree.
function countVendorsWithRules(vendorRules) {
  return Object.keys(vendorRules || {})
    .filter((v) => v && v.trim() && (vendorRules[v] || []).length > 0)
    .length;
}

// ── Top bar ─────────────────────────────────────────────────────────────
function TopBar({ view, onNav, theme, setTheme, vendorCount, user, job, onBackToJobs, userRole, onAdmin }) {
  const displayName = user?.displayName || user?.email?.split('@')[0] || '';
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const nav = (v) => { setMenuOpen(false); onNav(v); };

  // Close the mobile nav dropdown on outside-click or Escape.
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  return (
    <div className="topbar">
      <div className="brand">
        {/* UX #15 (a11y): decorative — the "KCGMatch" wordmark is adjacent, so empty alt avoids SR duplication. */}
        <img src="assets/KCG.webp" alt="" className="brand-logo" />
        <div className="brand-name">KCGMatch</div>
      </div>

      <div className="topbar-sep"></div>

      {/* Breadcrumb when inside a job */}
      {job && (
        <>
          <button className="topbar-back" onClick={onBackToJobs}>
            <Icon name="chev" size={11} style={{ transform: 'rotate(180deg)' }} />
            Jobs
          </button>
          <Icon name="chev" size={11} style={{ color: 'var(--fg-dim)' }} />
          <span className="topbar-job mono">{job.jobNumber}</span>
          <div className="topbar-sep"></div>
        </>
      )}

      {/* Breadcrumb for the Approvals queue (lives under Dashboard) */}
      {!job && view === 'approvals' && (
        <>
          <button className="topbar-back" onClick={() => onNav('dashboard')}>
            <Icon name="chev" size={11} style={{ transform: 'rotate(180deg)' }} />
            Dashboard
          </button>
          <Icon name="chev" size={11} style={{ color: 'var(--fg-dim)' }} />
          <span className="topbar-job">Approvals</span>
          <div className="topbar-sep"></div>
        </>
      )}

      {/* Global nav — segmented on desktop, hamburger dropdown on phones */}
      <div className="topbar-nav-wrap" ref={menuRef}>
        <button
          className="topbar-menu-btn"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Navigation menu" aria-expanded={menuOpen} aria-controls="topbar-navtabs"
        >
          <Icon name={menuOpen ? 'x' : 'menu'} size={16} />
        </button>
        <nav className={`navtabs ${menuOpen ? 'is-open' : ''}`} id="topbar-navtabs" role="tablist">
          {/* Dashboard owns the Approvals queue as a sub-view, so it stays
              selected while the user is in there. */}
          <button
            className={`navtab ${(view === 'dashboard' || view === 'approvals') ? 'is-on' : ''}`}
            role="tab" aria-selected={view === 'dashboard' || view === 'approvals'}
            onClick={() => nav('dashboard')}
          >
            <Icon name="grid" size={14}/>
            <span>Dashboard</span>
          </button>
          <button
            className={`navtab ${view === 'jobs' ? 'is-on' : ''}`}
            role="tab" aria-selected={view === 'jobs'}
            onClick={() => nav('jobs')}
          >
            <Icon name="file" size={14}/>
            <span>Jobs</span>
          </button>
          <button
            className={`navtab ${view === 'prices' ? 'is-on' : ''}`}
            role="tab" aria-selected={view === 'prices'}
            onClick={() => nav('prices')}
          >
            <Icon name="history" size={14}/>
            <span>Price History</span>
          </button>
          <button
            className={`navtab ${view === 'settings' ? 'is-on' : ''}`}
            role="tab" aria-selected={view === 'settings'}
            onClick={() => nav('settings')}
          >
            <Icon name="settings" size={14}/>
            <span>Settings</span>
            {vendorCount > 0 && <span className="navtab-count num">{vendorCount}</span>}
          </button>
        </nav>
      </div>

      <div className="topbar-spacer"></div>

      {user && window.APPROVALS_ENABLED && <NotificationBell userId={user.uid} onNavigate={() => onNav('approvals')} />}
      <ThemeSwitch value={theme} onChange={setTheme} />
      {userRole === 'ADMIN' && (
        <button className="topbar-admin-btn" onClick={onAdmin} title="Admin Settings">
          <Icon name="settings" size={15} />
        </button>
      )}
      {user && (
        <div className="topbar-user">
          {userRole && (
            <span className="topbar-role" style={{ '--rc': ROLE_COLOR?.[userRole] || 'var(--accent)' }}>
              {ROLE_LABEL[userRole] || userRole}
            </span>
          )}
          {displayName && <span className="topbar-uname">{displayName}</span>}
          <button className="btn btn-ghost btn-sm" onClick={() => fbAuth.signOut()}>Sign out</button>
        </div>
      )}

      <TopBarStyles />
      {/* Approval styles ship only while the workflow is enabled — the bundle
          is excluded from index.html when APPROVALS_ENABLED is false, so an
          unconditional reference here would be a ReferenceError. */}
      {window.APPROVALS_ENABLED && <ApprovalWorkflowStyles />}
    </div>
  );
}

// ── Job sub-nav (Summary / Materials / Invoices / Issues inside job context) ──
function JobSubNav({ tab, setTab, trades, selectedTrades, onScopeChange }) {
  return (
    <div className="job-subnav">
      {/* UX #13 (a11y): expose as a tablist (matches the global nav) so assistive
          tech announces these as tabs and the selected one. */}
      <div className="job-subnav-inner">
        <div className="jsn-tabs" role="tablist" aria-label="Job sections">
          <button
            className={`jsn-tab ${tab === 'summary' ? 'is-on' : ''}`}
            role="tab" aria-selected={tab === 'summary'}
            onClick={() => setTab('summary')}
          >
            <Icon name="delta" size={15}/>
            Summary
          </button>
          <button
            className={`jsn-tab ${tab === 'materials' ? 'is-on' : ''}`}
            role="tab" aria-selected={tab === 'materials'}
            onClick={() => setTab('materials')}
          >
            <Icon name="layers" size={15}/>
            Materials
          </button>
          <button
            className={`jsn-tab ${tab === 'invoices' ? 'is-on' : ''}`}
            role="tab" aria-selected={tab === 'invoices'}
            onClick={() => setTab('invoices')}
          >
            <Icon name="file" size={15}/>
            Invoices
          </button>
          <button
            className={`jsn-tab ${tab === 'issues' ? 'is-on' : ''}`}
            role="tab" aria-selected={tab === 'issues'}
            onClick={() => setTab('issues')}
          >
            <Icon name="flag" size={15}/>
            Issues
          </button>
        </div>
        {/* Per-trade view scope — renders nothing unless the job has 2+ trades. */}
        <TradeScopeSelector trades={trades} selected={selectedTrades} onChange={onScopeChange} />
      </div>
      <style>{`
        .job-subnav {
          background: var(--bg);
          border-bottom: 1px solid var(--border);
          position: sticky; top: 52px; z-index: 10;
        }
        .job-subnav-inner {
          max-width: var(--app-max); margin: 0 auto;
          padding: 0 var(--app-pad);
          display: flex; align-items: center; gap: 18px; flex-wrap: wrap;
        }
        .jsn-tabs {
          display: flex; gap: 0;
          overflow-x: auto;          /* 4 tabs can exceed phone width — scroll, don't clip */
          scrollbar-width: none;     /* Firefox */
        }
        .jsn-tabs::-webkit-scrollbar { display: none; }
        .jsn-tab {
          appearance: none; border: 0; background: transparent;
          padding: 11px 20px;
          font: inherit; font-size: 13.5px; font-weight: 500;
          color: var(--fg-muted); cursor: pointer;
          display: inline-flex; align-items: center; gap: 8px;
          flex-shrink: 0; white-space: nowrap;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: color .12s, border-color .12s;
        }
        .jsn-tab:hover:not(.is-on) { color: var(--fg); }
        .jsn-tab.is-on {
          color: var(--accent);
          border-bottom-color: var(--accent);
          font-weight: 600;
        }
        .jsn-tab.is-on svg { color: var(--accent); }
      `}</style>
    </div>
  );
}

const TopBarStyles = () => (
  <style>{`
    .navtabs { display: flex; gap: 2px; padding: 3px; background: var(--surface-2); border: 1px solid var(--border); border-radius: var(--r); }
    .navtab {
      appearance: none; border: 0;
      background-color: transparent;
      padding: 5px 12px; border-radius: 4px;
      font: inherit; font-size: 12.5px; font-weight: 500;
      color: var(--fg-muted); cursor: pointer;
      display: inline-flex; align-items: center; gap: 7px;
      transition: background-color .12s, color .12s;
    }
    .navtab:hover:not(.is-on) { color: var(--fg); background-color: oklch(from var(--fg) l c h / 0.06); }
    /* Bold active state: accent-tinted fill + accent text/icon (was grey-on-grey). */
    .navtab.is-on { background-color: oklch(from var(--accent) l c h / 0.14); color: var(--accent); font-weight: 600; }
    .navtab.is-on svg { color: var(--accent); }
    .navtab-count { font-size: 10.5px; padding: 1px 6px; border-radius: 999px; background: var(--surface-3); color: var(--fg-dim); }
    .navtab.is-on .navtab-count { background: oklch(from var(--accent) l c h / 0.22); color: var(--accent); }
    /* "Preview" pill on not-yet-shipped nav entries (e.g. History). */
    .navtab-soon {
      font-size: 9px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
      padding: 1px 5px; border-radius: 999px;
      background: oklch(from var(--info) l c h / 0.18); color: var(--info);
    }

    .theme-switch {
      appearance: none; border: 1px solid var(--border); background: var(--surface-2);
      padding: 3px; border-radius: 999px; cursor: pointer;
      display: inline-flex; align-items: center;
      transition: background .12s, border-color .12s;
    }
    .theme-switch:hover { background: var(--surface-3); }
    .theme-switch-track {
      position: relative; display: block; width: 38px; height: 18px;
    }
    .theme-switch-thumb {
      position: absolute; top: 0; bottom: 0; width: 18px;
      border-radius: 50%;
      display: grid; place-items: center;
      background: var(--fg); color: var(--bg);
      transition: left .18s cubic-bezier(.3,.7,.4,1), background .18s, color .18s;
    }
    .theme-switch-thumb[data-on="dark"] { left: 0; }
    .theme-switch-thumb[data-on="light"] { left: 20px; }

    .topbar-user { display: flex; align-items: center; gap: 8px; }
    .topbar-uname { font-size: 12.5px; color: var(--fg-muted); max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .topbar-role {
      font-size: 10px; padding: 2px 8px; border-radius: 999px;
      background: oklch(from var(--rc, var(--accent)) l c h / 0.15);
      color: var(--rc, var(--accent));
      border: 1px solid oklch(from var(--rc, var(--accent)) l calc(c*.5) h / .35);
      font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; white-space: nowrap;
    }
    .topbar-admin-btn {
      appearance: none; border: 1px solid var(--border); background: var(--surface-2);
      border-radius: var(--r); padding: 5px 7px; cursor: pointer; color: var(--fg-muted);
      display: grid; place-items: center;
      transition: background .12s, border-color .12s, color .12s;
    }
    .topbar-admin-btn:hover { background: var(--surface-3); border-color: var(--border-strong); color: var(--fg); }

    .topbar-back {
      appearance: none; border: 0; background: transparent; padding: 4px 6px;
      border-radius: var(--r); font: inherit; font-size: 12.5px; font-weight: 500;
      color: var(--fg-muted); cursor: pointer;
      display: inline-flex; align-items: center; gap: 5px;
      transition: color .12s, background .12s;
    }
    .topbar-back:hover { color: var(--fg); background: var(--surface-2); }
    .topbar-job { font-size: 12.5px; font-weight: 600; color: var(--fg); }

    /* UX #3 (responsive): on phones the global nav collapses into a hamburger
       dropdown so brand + breadcrumb + account controls still fit; the bar also
       scrolls horizontally as a final safety net. */
    .topbar-nav-wrap { display: flex; align-items: center; }
    .topbar-menu-btn {
      display: none; appearance: none; border: 1px solid var(--border);
      background: var(--surface-2); border-radius: var(--r); padding: 5px 7px;
      cursor: pointer; color: var(--fg-muted); place-items: center;
      transition: background .12s, border-color .12s, color .12s;
    }
    .topbar-menu-btn:hover { background: var(--surface-3); border-color: var(--border-strong); color: var(--fg); }
    @media (max-width: 640px) {
      .topbar { gap: 8px; padding: 0 12px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
      .topbar-uname, .topbar-role { display: none; }   /* free up width; identity still in Admin/account */
      .topbar-menu-btn { display: inline-grid; }
      /* Nav becomes a fixed dropdown — position:fixed escapes the bar's overflow clip. */
      .navtabs {
        position: fixed; top: 52px; left: 8px; right: auto;
        flex-direction: column; gap: 2px; min-width: 204px; padding: 6px;
        background: var(--surface); border: 1px solid var(--border);
        border-radius: var(--r-lg); box-shadow: var(--shadow-2);
        z-index: 90; display: none;
      }
      .navtabs.is-open { display: flex; }
      .navtab { width: 100%; justify-content: flex-start; padding: 9px 12px; font-size: 13.5px; }
    }
  `}</style>
);

// ── Settings (the matching/diagnostic internals, behind disclosures) ──────
// Everything that used to be its own top-level page (vendor rules, tolerances,
// admin) now lives here, collapsed by default — the app leads with answers and
// keeps the machinery one click away.
function SettingsView({ vendorRules, setVendorRules, userRole, onCompareWithVendor, onOpenAdmin }) {
  const vendorCount = countVendorsWithRules(vendorRules);

  return (
    <div className="set-view">
      <div className="set-hero">
        <div className="set-eyebrow mono">SETTINGS</div>
        <h1 className="set-h">Settings</h1>
        <p className="set-sub">
          Configuration that shapes how invoices are matched and reviewed. Open a section only when you
          need to tune it — the day-to-day work happens in Dashboard and Jobs.
        </p>
      </div>

      {/* Vendor rules — open by default since it's the one users actually edit. */}
      <details className="set-disc" open>
        <summary className="set-disc-sum">
          <Icon name="rule" size={15} />
          <span className="set-disc-t">Vendor rules &amp; matching context</span>
          <span className="set-disc-meta num">{vendorCount} vendor{vendorCount !== 1 ? 's' : ''}</span>
          <Icon name="chev" size={13} className="set-disc-chev" />
        </summary>
        <div className="set-disc-body">
          <p className="set-disc-desc">
            Notes and rules saved per vendor — ignores, tolerances, aliases, and context. They&rsquo;re
            pre-applied to every comparison against that vendor.
          </p>
          <VendorsView
            vendorRules={vendorRules}
            setVendorRules={setVendorRules}
            onCompareWithVendor={onCompareWithVendor}
            embedded
          />
        </div>
      </details>

      {/* Admin — only for admins. Opens the existing full admin panel. */}
      {userRole === 'ADMIN' && (
        <details className="set-disc">
          <summary className="set-disc-sum">
            <Icon name="settings" size={15} />
            <span className="set-disc-t">Administration</span>
            <span className="set-disc-meta">Admins only</span>
            <Icon name="chev" size={13} className="set-disc-chev" />
          </summary>
          <div className="set-disc-body">
            <p className="set-disc-desc">Manage team members, roles, and Firestore security rules.</p>
            <button className="btn btn-primary btn-sm" onClick={onOpenAdmin}>
              <Icon name="settings" size={14} /> Open admin settings
            </button>
          </div>
        </details>
      )}

      <SettingsViewStyles />
    </div>
  );
}

const SettingsViewStyles = () => (
  <style>{`
    /* Match the shared page frame (--app-max / --app-pad) used by Dashboard,
       Jobs, Price History, and the in-job views so the content width stays
       consistent when switching nav tabs. Inner text blocks keep their own
       narrower max-widths (set-hero / set-disc-desc) for readability.
       width: 100% is load-bearing on every view root: .app is a column flexbox,
       and a flex item with auto side margins is NOT stretched — it shrinks to
       its content. Without it each screen was as wide as whatever it happened
       to contain (DESIGN-SYSTEM.md → Page width). */
    .set-view { width: 100%; max-width: var(--app-max); margin: 0 auto; padding: 48px var(--app-pad) 80px; display: flex; flex-direction: column; gap: 14px; }
    .set-hero { margin-bottom: 10px; max-width: 720px; }
    .set-eyebrow { font-size: 11px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent); margin-bottom: 12px; }
    .set-h { font-size: 30px; line-height: 1.1; letter-spacing: -0.025em; margin: 0 0 10px; font-weight: 600; }
    .set-sub { color: var(--fg-muted); font-size: 14px; line-height: 1.55; margin: 0; }

    .set-disc { border: 1px solid var(--border); border-radius: var(--r-lg); background: var(--surface); overflow: hidden; box-shadow: var(--shadow-1); }
    .set-disc-sum {
      display: flex; align-items: center; gap: 11px; cursor: pointer; list-style: none;
      padding: 16px 20px; font-size: 14px; font-weight: 600; color: var(--fg);
      user-select: none; transition: background .12s;
    }
    .set-disc-sum::-webkit-details-marker { display: none; }
    .set-disc-sum:hover { background: var(--surface-2); }
    .set-disc-sum > svg:first-child { color: var(--accent); flex-shrink: 0; }
    .set-disc-t { flex: 1; }
    .set-disc-meta {
      font-size: 11px; font-weight: 500; color: var(--fg-dim);
      padding: 2px 9px; border-radius: 999px; background: var(--surface-2); border: 1px solid var(--border);
    }
    .set-disc-chev { color: var(--fg-dim); transition: transform .15s; flex-shrink: 0; }
    .set-disc[open] .set-disc-chev { transform: rotate(90deg); }
    .set-disc-body { padding: 4px 20px 20px; border-top: 1px solid var(--border); }
    .set-disc-desc { font-size: 13px; color: var(--fg-muted); line-height: 1.55; margin: 14px 0 2px; max-width: 640px; }
  `}</style>
);

// The built-in demo job (Tweaks → "Load sample job"). Not in Firestore, so a
// #/jobs/_sample URL resolves to this instead of the jobs store.
const SAMPLE_JOB = { id: '_sample', jobNumber: 'SAMPLE' };

// Centered spinner: full-screen while auth resolves, `inline` under the top bar.
const PageLoader = ({ inline = false }) => (
  <div className="auth-loader" style={inline ? { minHeight: '60vh' } : undefined}>
    <div className="auth-loader-spin"></div>
    <style>{`
      .auth-loader { display:flex; align-items:center; justify-content:center; min-height:100vh; background:var(--bg); }
      .auth-loader-spin { width:28px; height:28px; border-radius:50%; border:2.5px solid var(--border); border-top-color:var(--accent); animation:spin .7s linear infinite; }
      @keyframes spin { to { transform:rotate(360deg); } }
    `}</style>
  </div>
);

// ── App root ────────────────────────────────────────────────────────────
function App() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const user = useAuthState();

  // ── vendorRules — Firebase-backed ─────────────────────────────────────
  // Dual-mode via the shared hook (invodiff-data.jsx): team-wide /vendorRules
  // collection once migrated (window.__migrateVendorRules), per-user map until
  // then. Same { vendorRules, setVendorRules } contract for every consumer.
  const { vendorRules, setVendorRules } = useVendorRules(user, t.seedHistory);

  // ── User role ──────────────────────────────────────────────────────────
  const [userRole,   setUserRole]   = React.useState(null);
  const [authError,  setAuthError]  = React.useState(null);

  React.useEffect(() => {
    if (!user) { setUserRole(null); return; }
    const unsub = fbDb.collection('users').doc(user.uid).onSnapshot((doc) => {
      if (doc.exists) {
        const data = doc.data();
        if (data.status === 'deleted') {
          setAuthError('Your account has been removed. Contact your administrator.');
          fbAuth.signOut();
          return;
        }
        // Role may be null (no role assigned) — that is valid, don't re-bootstrap.
        setUserRole(data.role || null);
      } else {
        // SECURITY (audit #1): previously, a first-time user with no Firestore
        // doc was auto-promoted to ADMIN client-side. Any new account could
        // therefore become an admin simply by signing in. New users now start
        // with NO role and must be provisioned by an existing admin. The very
        // first admin must be set manually (Firebase console or a one-off
        // script) — the hardened rules also reject any client-side role write.
        // The doc is created without privileged fields by the effect below.
        setUserRole(null);
      }
    }, () => setUserRole(null));
    return unsub;
  }, [user?.uid]);

  const userInfo = React.useMemo(() => user ? ({
    uid:         user.uid,
    displayName: user.displayName || user.email?.split('@')[0] || user.email,
    email:       user.email,
    role:        userRole,
  }) : null, [user, userRole]);

  // ── UI state ──────────────────────────────────────────────────────────
  // route: where the user is, mirrored in the URL hash (src/route.js) so the
  //        browser's back / forward buttons, reload and bookmarks all work.
  //   view: global tab — 'dashboard' | 'jobs' | 'prices' | 'settings' | 'approvals'
  //         ('approvals' is a Dashboard sub-view, reached from its card / the bell)
  //   jobId: currently open job (null = list view)
  //   jobTab: sub-tab within a job — 'summary' | 'materials' | 'invoices' | 'issues'
  // jobObj: the job object for route.jobId. Set directly when a job is clicked;
  //         looked up in the shared jobs store when the route arrived some other
  //         way (back / forward, reload, a pasted link).
  const [route, go]             = useRoute();
  const [jobObj, setJobObj]     = React.useState(null);
  const { jobs: storeJobs }     = useJobsStore();
  const view   = route.view === 'approvals' && !window.APPROVALS_ENABLED ? 'dashboard' : route.view;
  const jobTab = route.jobTab;
  const job    = route.jobId && jobObj && jobObj.id === route.jobId ? jobObj : null;
  const jobPending = !!route.jobId && !job;
  const [adminOpen, setAdminOpen] = React.useState(false);
  const [focusInvoice, setFocusInvoice] = React.useState(null); // invoice id to highlight in the Invoices tab
  // selectedTrades: which trade segments the job tabs show; null = all trades.
  // Only meaningful for jobs that cover 2+ trades (see TradeScopeSelector).
  const [selectedTrades, setSelectedTrades] = React.useState(null);

  // Write display name + email to Firestore so the admin panel can identify users
  React.useEffect(() => {
    if (!user) return;
    fbDb.collection('users').doc(user.uid).set({
      email:       user.email       || '',
      displayName: user.displayName || user.email?.split('@')[0] || '',
    }, { merge: true }).catch(() => {});
  }, [user?.uid]);

  React.useEffect(() => {
    document.documentElement.dataset.theme = t.theme;
    document.documentElement.dataset.density = t.density;
    const accentMap = t.theme === 'light' ? ACCENT_MAP_LIGHT : ACCENT_MAP;
    document.documentElement.style.setProperty('--accent', accentMap[t.accent] || accentMap.steel);
  }, [t.theme, t.density, t.accent]);

  const setTheme = (v) => setTweak('theme', v);

  // Resolve the job a route names when it wasn't just clicked. Waits for the
  // jobs store's first snapshot; an id that matches nothing (deleted job, bad
  // link) falls back to the jobs list without leaving a dead history entry.
  React.useEffect(() => {
    if (!jobPending) return;
    if (route.jobId === SAMPLE_JOB.id) { setJobObj(SAMPLE_JOB); return; }
    if (!storeJobs) return;
    const found = storeJobs.find((j) => j.id === route.jobId);
    if (found) setJobObj(found);
    else go({ view: 'jobs' }, { replace: true });
  }, [jobPending, route.jobId, storeJobs, go]);

  // The trade scope belongs to one visit to one job.
  React.useEffect(() => { setSelectedTrades(null); }, [route.jobId]);

  const setJobTab = (tab) => go({ view: 'jobs', jobId: route.jobId, jobTab: tab });

  // Navigate to a global tab (always exits job context)
  const handleNav = (targetView) => {
    setFocusInvoice(null);
    go({ view: targetView });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectJob = (j) => {
    setJobObj(j);
    setFocusInvoice(null);
    go({ view: 'jobs', jobId: j.id, jobTab: 'summary' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Deep-link from Price History → the source invoice: open that job's Invoices
  // tab and highlight the specific invoice so the estimator can see why its
  // prices differ from other invoices.
  const handleOpenJobInvoice = (j, invoiceId) => {
    setJobObj(j);
    setFocusInvoice(invoiceId || null);
    go({ view: 'jobs', jobId: j.id, jobTab: 'invoices' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToJobs = () => {
    go({ view: 'jobs' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const vendorCount = countVendorsWithRules(vendorRules);

  // ── Auth gate ─────────────────────────────────────────────────────────
  if (user === undefined) return <PageLoader />;
  if (!user) return <LoginScreen authError={authError} onClearError={() => setAuthError(null)} />;

  return (
    <div className="app">
      <TopBar
        view={view}
        onNav={handleNav}
        theme={t.theme}
        setTheme={setTheme}
        vendorCount={vendorCount}
        user={user}
        job={job}
        onBackToJobs={handleBackToJobs}
        userRole={userRole}
        onAdmin={() => setAdminOpen(true)}
      />

      {/* A job URL opened cold (reload, pasted link): the jobs store is still
          loading, so there is nothing to show yet. */}
      {jobPending && <PageLoader inline />}

      {/* Global: Dashboard (portfolio overview + Price History widget) */}
      {!route.jobId && view === 'dashboard' && (
        <ErrorBoundary level="view" name="dashboard" resetKey={view}>
          <JobListView mode="dashboard" user={user} onSelectJob={handleSelectJob} onNav={handleNav} vendorRules={vendorRules} onOpenJobInvoice={handleOpenJobInvoice} />
        </ErrorBoundary>
      )}

      {/* Global: Jobs list */}
      {!route.jobId && view === 'jobs' && (
        <ErrorBoundary level="view" name="jobs list" resetKey={view}>
          <JobListView mode="jobs" user={user} onSelectJob={handleSelectJob} onNav={handleNav} vendorRules={vendorRules} />
        </ErrorBoundary>
      )}

      {/* Global: Approvals queue (a Dashboard sub-view) */}
      {!route.jobId && view === 'approvals' && window.APPROVALS_ENABLED && (
        <ErrorBoundary level="view" name="approvals" resetKey={view}>
          <ApprovalView user={user} userInfo={userInfo} />
        </ErrorBoundary>
      )}

      {/* Global: Price History (cross-job material price tracking) */}
      {!route.jobId && view === 'prices' && (
        <ErrorBoundary level="view" name="price history" resetKey={view}>
          <PriceHistoryView user={user} vendorRules={vendorRules} onOpenJob={handleOpenJobInvoice} />
        </ErrorBoundary>
      )}

      {/* Global: Settings (vendor rules, matching tolerances, admin) */}
      {!route.jobId && view === 'settings' && (
        <ErrorBoundary level="view" name="settings" resetKey={view}>
          <SettingsView
            vendorRules={vendorRules}
            setVendorRules={setVendorRules}
            userRole={userRole}
            onCompareWithVendor={() => handleNav('jobs')}
            onOpenAdmin={() => setAdminOpen(true)}
          />
        </ErrorBoundary>
      )}

      {/* Job context: sub-nav + Summary / Materials / Invoices / Issues */}
      {/* Keyed by job: the in-job views hold per-job state (uploads in flight,
          expanded rows, a derived Procore link), so moving from one job straight
          to another — possible now that jobs have URLs — starts them fresh
          rather than carrying the previous job's state across. */}
      {job && (
        <React.Fragment key={job.id}>
          <JobSubNav
            tab={jobTab} setTab={setJobTab}
            trades={getJobTrades(job)}
            selectedTrades={selectedTrades}
            onScopeChange={setSelectedTrades}
          />
          {jobTab === 'summary' && (
            <ErrorBoundary level="view" name="Summary" resetKey={job.id}>
              <SummaryView
                job={job}
                userId={user.uid}
                vendorRules={vendorRules}
                onNavTab={setJobTab}
                selectedTrades={selectedTrades}
              />
            </ErrorBoundary>
          )}
          {jobTab === 'materials' && (
            <ErrorBoundary level="view" name="Materials" resetKey={job.id}>
              <DrawdownView
                job={job}
                userId={user.uid}
                vendorRules={vendorRules}
                selectedTrades={selectedTrades}
              />
            </ErrorBoundary>
          )}
          {jobTab === 'invoices' && (
            <ErrorBoundary level="view" name="Invoices" resetKey={job.id}>
              <InputView
                job={job}
                userId={user.uid}
                vendorRules={vendorRules}
                userInfo={userInfo}
                focusInvoice={focusInvoice}
                onFocusConsumed={() => setFocusInvoice(null)}
              />
            </ErrorBoundary>
          )}
          {jobTab === 'issues' && (
            <ErrorBoundary level="view" name="Issues" resetKey={job.id}>
              <VarianceView
                job={job}
                userId={user.uid}
                vendorRules={vendorRules}
                setVendorRules={setVendorRules}
                selectedTrades={selectedTrades}
              />
            </ErrorBoundary>
          )}
        </React.Fragment>
      )}

      {adminOpen && (
        <ErrorBoundary level="view" name="Admin" resetKey={adminOpen}>
          <AdminPanel currentUser={userInfo} onClose={() => setAdminOpen(false)} />
        </ErrorBoundary>
      )}

      <TweaksPanel title="Tweaks">
        <TweakSection label="Appearance" />
        <TweakRadio label="Theme" value={t.theme}
                    options={['dark', 'light']}
                    onChange={(v) => setTweak('theme', v)} />
        <TweakRadio label="Density" value={t.density}
                    options={['compact', 'comfortable']}
                    onChange={(v) => setTweak('density', v)} />
        <TweakColor label="Accent" value={ACCENT_MAP[t.accent] || ACCENT_MAP.steel}
                    options={Object.values(ACCENT_MAP)}
                    onChange={(hex) => {
                      const entry = Object.entries(ACCENT_MAP).find(([, v]) => v === hex);
                      if (entry) setTweak('accent', entry[0]);
                    }} />

        <TweakSection label="Data" />
        <TweakToggle label="Seed vendor history" value={t.seedHistory}
                     onChange={(v) => setTweak('seedHistory', v)} />
        <TweakButton label="Load sample job" onClick={() => {
          handleSelectJob(SAMPLE_JOB);
        }} />
        <TweakButton secondary label="Clear all" onClick={() => {
          go({ view: 'dashboard' });
        }} />
      </TweaksPanel>
    </div>
  );
}

// Top-level safety net: any uncaught render error shows a recoverable full-page
// fallback instead of blanking the app. Per-view boundaries (below, in App)
// keep a single crashing screen from taking down the whole UI.
createRoot(document.getElementById('root')).render(
  <ErrorBoundary level="app">
    <App />
  </ErrorBoundary>
);
